import { ICopyable } from '../core/copyable.interface';
import { ITchatUser } from './interface/tchat-user.interface';

export class TchatUser implements ICopyable<TchatUser> {
    login: string = null;
    displayName: string = null;
    initials: string = null;
    color: string = null;
    online: boolean = null;
    lastConnection: Date = null;
    notifications: number = 0;

    constructor(login: string = null, displayName: string = null, online: boolean = null, lastConnection: Date = null, initial: string = null, color = '') {
        this.login = login;
        this.displayName = displayName;
        this.initials = !!initial ? initial : this.getInitials();
        this.color = !!color ? color : this.getColor();
        this.online = online;
        this.lastConnection = lastConnection;
        this.notifications = 0;
    }

    public createCopy(): TchatUser {
        const instance = new TchatUser();
        instance.login = this.login;
        instance.displayName = this.displayName;
        instance.initials = this.initials;
        instance.online = this.online;
        instance.lastConnection = this.lastConnection;
        return instance;
    }

    public static createFormCopy(chatUser: TchatUser): ITchatUser {
        const instance = new TchatUser();
        instance.login = chatUser.login;
        instance.displayName = chatUser.displayName;
        instance.initials = chatUser.initials;
        instance.online = chatUser.online;
        instance.lastConnection = chatUser.lastConnection;
        return instance;
    }

    public copyFrom(other: any) {
        this.login = other.login;
        this.displayName = other.displayName;
        this.initials = other.displayName;
        this.online = other.online;
        this.lastConnection = other.lastConnection;
    }

    /** Recupère les initiales d'un display name */
    private getInitials(): string {
        let initials = '';
        const names = this.displayName.split(' ');
        names.forEach((n) => {
            initials = `${initials}${n.charAt(0)}`;
        });
        return initials;
    }

    /**
     * Génère une couleur à partir du nom de l'utilisateur.
     */
    private getColor(): string {
        let hashCode = this.hashCode(this.displayName);
        let hashCode2 = this.hashCode(this.login);

        const hslColor = this.getHslColor(hashCode, hashCode2);

        return `hsl(${hslColor[0]}, ${hslColor[1]}%, ${hslColor[2]}%)`;
    }

    /**
     * Génère une couleur au format HSL dans les tons pastels à partir de 2 nombres.
     * @returns Un tableau de nombre contenant la valeur de la teinte, de la saturation et de la luminosité.
     */
    private getHslColor(value1: number, value2: number): number[] {
        let hueValue = this.getHueValue(value1);

        /**
         * Pour garantir des couleurs pastels, la saturation est comprise entre 30 et 65 et la luminosité est comprise entre la valeur de la saturation + ou - 5.
         */
        let saturationValue = 30 + Math.floor(Math.abs(value2) % 35);
        let lightValue = saturationValue - 5 + Math.floor((Math.abs(value1) - Math.abs(value2)) % 10);

        return [hueValue, saturationValue, lightValue];
    }

    /**
     * Retourne un hashcode pour une chaîne de caractères.
     * @param value : Chaîne de caractères dont on veut le hashcode.
     * @returns : Le hashcode.
     */
    private hashCode(value: string): number {
        let h = 0;
        for (let i = 0; i < value.length; i++) h = (Math.imul(31, h) + value.charCodeAt(i)) | 0;

        return h;
    }

    /**
     * Retourne la teinte correpondante à un nombre.
     * @param value1 : Nombre à partir duquel la teinte est déterminée.
     * @returns : La valeur de la teinte (il s'agit d'une valeur comprise entre 0 et 360).
     */
    private getHueValue(value1: number): number {
        let hueValue = 0;

        while (value1 > 360) {
            hueValue += Math.floor(Math.abs(value1) % 360);
            value1 = Math.floor(Math.abs(value1) / 360);
        }

        return hueValue % 360;
    }
}
